import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

const SESSION_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour
const LOGIN_AT_KEY = "cloudease.admin.loginAt";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  loading: boolean;
  aal: "aal1" | "aal2" | null;
  refreshAal: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [aal, setAal] = useState<"aal1" | "aal2" | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAdmin = async (userId: string) => {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();
    setIsAdmin(!!data);
    return !!data;
  };

  const refreshAal = useCallback(async () => {
    const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    setAal((data?.currentLevel as "aal1" | "aal2" | null) ?? null);
  }, []);

  const forceSignOut = useCallback(async () => {
    localStorage.removeItem(LOGIN_AT_KEY);
    await supabase.auth.signOut();
  }, []);

  // Enforce 1-hour absolute session lifetime for admins
  useEffect(() => {
    if (!user || !isAdmin) return;
    const check = () => {
      const raw = localStorage.getItem(LOGIN_AT_KEY);
      const loginAt = raw ? parseInt(raw, 10) : NaN;
      if (!raw || Number.isNaN(loginAt) || Date.now() - loginAt > SESSION_MAX_AGE_MS) {
        forceSignOut();
      }
    };
    check();
    const id = window.setInterval(check, 30_000);
    return () => window.clearInterval(id);
  }, [user, isAdmin, forceSignOut]);

  useEffect(() => {
    let mounted = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        setLoading(true);
        setTimeout(async () => {
          await checkAdmin(session.user.id);
          await refreshAal();
          if (mounted) setLoading(false);
        }, 0);
      } else {
        setIsAdmin(false);
        setAal(null);
        localStorage.removeItem(LOGIN_AT_KEY);
        setLoading(false);
      }
    });

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!mounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        await checkAdmin(session.user.id);
        await refreshAal();
      }
      if (mounted) setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [refreshAal]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error) {
      localStorage.setItem(LOGIN_AT_KEY, Date.now().toString());
    }
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await forceSignOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, isAdmin, loading, aal, refreshAal, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
